import { displayFileNameFromObjectKey } from "../lib/file-name";

/** The value carried by a `document` cell is the storage `objectKey`. */
export type DocumentValue = string | null;

export interface DocumentOptions {
  /**
   * Optional explicit display filename. Used only when the value
   * (`objectKey`) is missing — for surfaces that have already resolved
   * a display name out-of-band.
   */
  filename?: string | null;
  contentType?: string | null;
  onClick?: () => void;
}

export function resolveDocumentDisplayName(
  value: DocumentValue,
  options: DocumentOptions | undefined,
): string {
  if (value != null && value !== "") {
    return displayFileNameFromObjectKey(value);
  }
  return options?.filename ?? "";
}

/**
 * Middle-truncate a filename, preserving the extension. e.g.
 * `very-long-filename-with-stuff.pdf` → `very-long-fil…stuff.pdf`.
 */
export function middleTruncateFilename(name: string, maxChars: number): string {
  if (name.length <= maxChars) return name;
  const dotIdx = name.lastIndexOf(".");
  const ext = dotIdx > 0 && dotIdx >= name.length - 8 ? name.slice(dotIdx) : "";
  const stem = ext ? name.slice(0, dotIdx) : name;
  const keep = Math.max(maxChars - ext.length - 1, 4);
  const head = Math.ceil(keep * 0.6);
  const tail = keep - head;
  if (stem.length <= keep) return `${stem}${ext}`;
  return `${stem.slice(0, head)}…${stem.slice(stem.length - tail)}${ext}`;
}

/**
 * Guess the MIME content type from a filename's extension. Exported for
 * tests.
 */
export function guessContentTypeFromFilename(filename: string): string | undefined {
  const ext = filename.toLowerCase().split(".").pop();
  if (!ext) return undefined;
  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "doc":
      return "application/msword";
    case "xlsx":
    case "xls":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "csv":
      return "text/csv";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
      return `image/${ext === "jpg" ? "jpeg" : ext}`;
    case "txt":
    case "log":
    case "md":
      return "text/plain";
    case "json":
      return "application/json";
    default:
      return undefined;
  }
}
